"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { GetSitemapQuery } from "apollo"
import { useTranslations } from "next-intl"
import { CustomBreadcrumbs } from "ui"
import Container from "ui/Container"
import { extractProperty, getNonUmbracoUrl, Link } from "utils"

const StyledLayout = styled.div`
  padding-top: 32px;
  padding-bottom: 48px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 64px;
    padding-top: 48px;
    padding-bottom: 88px;
  }
`

const LinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  gap: 32px;

  ${(p) => p.theme.breakpoints.up("md")} {
    justify-content: space-between;
    grid-template-columns: repeat(4, auto);
  }
`

const LinksColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: fit-content;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  padding-bottom: 6px;
  :hover {
    box-shadow: inset 0 -1px 0 0 #051711;
  }
`

const excludedNames = ["Home", "Traffic updates"]

export default function SitemapComp({ data }: { data: GetSitemapQuery }) {
  const nodes = data.sitemap?.nodes || []
  const tInner = useTranslations("InnerPages")
  const filteredLinks = nodes
    .filter((el) => !excludedNames.includes(el?.name || ""))
    .map((el) => {
      const children =
        el?.children?.map((child) => ({
          title: extractProperty(child, "titleBreadcrumbs"),
          url: getNonUmbracoUrl(child?.url || ""),
        })) || []
      return {
        title: extractProperty(el, "titleBreadcrumbs"),
        url: getNonUmbracoUrl(el?.url || ""),
        children,
      }
    }) as {
    title: string
    url: string
    children: { title: string; url: string }[]
  }[]
  const singlePages = filteredLinks.filter((el) => !el?.children?.length)
  const arrayPages = filteredLinks.filter((el) => !!el?.children?.length)
  return (
    <Container paddingMobile>
      <StyledLayout>
        <CustomBreadcrumbs
          noPadding
          breadcrumbs={{
            selfObject: { title: tInner("sitemap"), url: "/sitemap" },
            parents: [{ title: tInner("homepage"), url: "/" }],
          }}
        />
        <Typography variant="h2">{tInner("sitemap")}</Typography>
        <LinksGrid>
          {arrayPages.map((el, i) => (
            <LinksColumn key={i}>
              <Typography variant="body1" fontWeight={700}>
                {el?.title}
              </Typography>
              {el.children.map((child, i) => (
                <StyledLink href={child.url} key={i}>
                  <Typography variant="body2">{child.title}</Typography>
                </StyledLink>
              ))}
            </LinksColumn>
          ))}
        </LinksGrid>
        <LinksColumn>
          {singlePages.map((el, i) => (
            <StyledLink href={el.url} key={i}>
              <Typography variant="body2">{el.title}</Typography>
            </StyledLink>
          ))}
        </LinksColumn>
      </StyledLayout>
    </Container>
  )
}
